import React from "react";
import { AlertCircle, Briefcase, Video, Link2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import ProfileLinks from "./ProfileLinks";
import ProfileSponsors from "./ProfileSponsors";
import { categories } from "@/types/tool";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ProfileContentProps {
  isEditing: boolean;
  status: string;
  categories: string[];
  videoUrl: string;
  links: { url: string; title: string; }[];
  sponsors: { name: string; logo: string; }[];
  onStatusChange: (value: string) => void;
  onCategoriesChange: (value: string[]) => void;
  onVideoUrlChange: (value: string) => void;
  onAddLink: (link: { url: string; title: string; }) => void;
  newLink: { url: string; title: string; };
  onNewLinkChange: (link: { url: string; title: string; }) => void;
}

const ProfileContent = ({
  isEditing,
  status,
  categories: selectedCategories,
  videoUrl,
  links,
  sponsors,
  onStatusChange,
  onCategoriesChange,
  onVideoUrlChange,
  onAddLink,
  newLink,
  onNewLinkChange,
}: ProfileContentProps) => {
  
  const handleCategoryToggle = (categoryName: string) => {
    const updatedCategories = selectedCategories.includes(categoryName)
      ? selectedCategories.filter(s => s !== categoryName)
      : [...selectedCategories, categoryName];
    onCategoriesChange(updatedCategories);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {isEditing ? (
        <>
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-gray-700">
              <AlertCircle className="w-4 h-4 text-purple-500" />
              Status Update
            </Label>
            <Input
              value={status}
              onChange={(e) => onStatusChange(e.target.value)}
              placeholder="What's on your mind?"
              className="bg-white/90 rounded-xl border-gray-200"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-gray-700">
              <Video className="w-4 h-4 text-purple-500" />
              Featured Video URL
            </Label>
            <Input
              value={videoUrl}
              onChange={(e) => onVideoUrlChange(e.target.value)}
              placeholder="Add your TikTok or YouTube video URL"
              className="bg-white/90 rounded-xl border-gray-200"
            />
          </div>

          <ProfileLinks
            links={links}
            isEditing={isEditing}
            onAddLink={onAddLink}
            newLink={newLink}
            onNewLinkChange={onNewLinkChange}
          />
        </>
      ) : (
        <>
          {status && (
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-purple-700 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium">Current Status</span>
              </div>
              <p className="text-purple-900">{status}</p>
            </div>
          )}

          <ProfileLinks
            links={links}
            isEditing={isEditing}
            newLink={newLink}
            onNewLinkChange={onNewLinkChange}
          />

          <ProfileSponsors sponsors={sponsors} />
        </>
      )}
    </div>
  );
};

export default ProfileContent;